import { Component, input, output, ChangeDetectionStrategy, signal, computed, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LucideAngularModule, ArrowUp, Mic, X, Check, Plus } from "lucide-angular";
import { injectChatLabels } from "../core/chat-labels";
import { CopilotTooltip } from "../directives/tooltip";
import { cn } from "../utils/utils";

@Component({
  standalone: true,
  selector: "agent-chat-send-button",
  imports: [CommonModule, LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="achat-toolbar__send-wrap">
      <button type="button" [disabled]="disabled()" [class]="buttonClass" (click)="onClick()">
        <lucide-angular [img]="ArrowUpIcon" [size]="18"></lucide-angular>
      </button>
    </div>
  `,
})
export class AgentChatSendButton {
  disabled = input(false);

  clicked = output<void>();

  readonly ArrowUpIcon = ArrowUp;

  buttonClass = "achat-btn achat-btn--primary";

  onClick(): void {
    if (!this.disabled) {
      this.clicked.emit();
    }
  }
}

@Component({
  standalone: true,
  selector: "agent-chat-start-transcribe-button",
  imports: [CommonModule, LucideAngularModule, CopilotTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      type="button"
      [disabled]="disabled()"
      [class]="buttonClass"
      [copilotTooltip]="label"
      tooltipPosition="below"
      (click)="onClick()"
    >
      <lucide-angular [img]="MicIcon" [size]="18"></lucide-angular>
    </button>
  `,
})
export class AgentChatStartTranscribeButton {
  disabled = input(false);

  clicked = output<void>();

  readonly labels = injectChatLabels();

  readonly MicIcon = Mic;

  buttonClass = "achat-btn achat-btn--secondary achat-btn--mr";

  get label(): string {
    return this.labels.chatInputToolbarStartTranscribeButtonLabel;
  }

  onClick(): void {
    if (!this.disabled()) {
      this.clicked.emit();
    }
  }
}

@Component({
  standalone: true,
  selector: "agent-chat-cancel-transcribe-button",
  imports: [CommonModule, LucideAngularModule, CopilotTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      type="button"
      [disabled]="disabled()"
      [class]="buttonClass"
      [copilotTooltip]="label"
      tooltipPosition="below"
      (click)="onClick()"
    >
      <lucide-angular [img]="XIcon" [size]="18"></lucide-angular>
    </button>
  `,
})
export class AgentChatCancelTranscribeButton {
  disabled = input(false);

  clicked = output<void>();

  readonly labels = injectChatLabels();

  readonly XIcon = X;

  buttonClass = "achat-btn achat-btn--secondary achat-btn--mr";

  get label(): string {
    return this.labels.chatInputToolbarCancelTranscribeButtonLabel;
  }

  onClick(): void {
    if (!this.disabled()) {
      this.clicked.emit();
    }
  }
}

@Component({
  standalone: true,
  selector: "agent-chat-finish-transcribe-button",
  imports: [CommonModule, LucideAngularModule, CopilotTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      type="button"
      [disabled]="disabled()"
      [class]="buttonClass"
      [copilotTooltip]="label"
      tooltipPosition="below"
      (click)="onClick()"
    >
      <lucide-angular [img]="CheckIcon" [size]="18"></lucide-angular>
    </button>
  `,
})
export class AgentChatFinishTranscribeButton {
  disabled = input(false);

  clicked = output<void>();

  readonly labels = injectChatLabels();

  readonly CheckIcon = Check;

  buttonClass = "achat-btn achat-btn--secondary achat-btn--mr-custom";

  get label(): string {
    return this.labels.chatInputToolbarFinishTranscribeButtonLabel;
  }

  onClick(): void {
    if (!this.disabled()) {
      this.clicked.emit();
    }
  }
}

@Component({
  standalone: true,
  selector: "agent-chat-add-file-button",
  imports: [CommonModule, LucideAngularModule, CopilotTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      type="button"
      [disabled]="disabled()"
      [class]="buttonClass"
      [copilotTooltip]="label"
      tooltipPosition="below"
      (click)="onClick()"
    >
      <lucide-angular [img]="PlusIcon" [size]="20"></lucide-angular>
    </button>
  `,
})
export class AgentChatAddFileButton {
  disabled = input(false);

  clicked = output<void>();

  readonly labels = injectChatLabels();

  readonly PlusIcon = Plus;

  buttonClass = "achat-btn achat-btn--secondary achat-btn--ml";

  get label(): string {
    return this.labels.chatInputToolbarAddButtonLabel;
  }

  onClick(): void {
    if (!this.disabled()) {
      this.clicked.emit();
    }
  }
}

@Component({
  standalone: true,
  selector: "agent-chat-toolbar-button",
  imports: [CommonModule, CopilotTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      type="button"
      [disabled]="disabled()"
      [class]="computedClass()"
      [copilotTooltip]="title()"
      tooltipPosition="below"
      (click)="onClick()"
    >
      <ng-content></ng-content>
    </button>
  `,
})
export class AgentChatToolbarButton {
  disabled = signal(false);

  variant = signal<"primary" | "secondary">("secondary");

  customClass = signal("");

  title = signal("");

  clicked = output<void>();

  computedClass = computed(() => {
    const variantClass = this.variant() === "primary" ? "achat-btn--primary" : "achat-btn--secondary";
    return cn("achat-btn", variantClass, this.customClass());
  });

  onClick(): void {
    if (!this.disabled()) {
      this.clicked.emit();
    }
  }
}
